import { Trans, useLingui } from "@lingui/react/macro";
import { Group, Popover, Progress, Text } from "@mantine/core";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import PlanBadge from "@/components/common/ui/badge/PlanBadge/PlanBadge";
import { crewStore } from "@/mobx/rootStore";
import { planQueries } from "@/utils/useQuery/queries/queries";
import { useFetchFromQuery } from "@/utils/useQuery/useFetch";
import classes from "./UsageLimit.module.css";
import { calcUsagePercent, getUsageColor, toISODate, useFeatureLabels } from "./usageUtils";

/**
 * Muestra el uso actual de un límite (p. ej. 5/30) con color según el porcentaje.
 * Al pasar el ratón o hacer click abre un Popover con detalles y una barra de progreso.
 *
 * @param {{ feature: string, date?: Date | string }} props
 */
const UsageLimit = observer(function UsageLimit({ feature, date }) {
	const { t } = useLingui();
	const [opened, setOpened] = useState(false);
	const featureLabels = useFeatureLabels();

	const crewId = crewStore.getSelectedCrew("id");
	const dateParam = toISODate(date);

	const { data, isLoading } = useFetchFromQuery(planQueries.getUsage, {
		args: { crewId, date: dateParam },
		enabled: Boolean(crewId),
	});

	if (isLoading || !data || !feature) return null;

	/** @type {{ feature: string, label: string, used: number, limit: number, period: string, yearMonth?: string } | undefined} */
	const item = data.items?.find(
		(/** @type {{ feature: string }} */ it) => it.feature === feature,
	);

	if (!item) return null;

	const percent = calcUsagePercent(item.used, item.limit);
	const color = getUsageColor(percent);

	const nextPlan = data.nextPlan ?? null;

	const periodLabel = item.yearMonth
		? t`Has usado ${item.used} de ${item.limit} este mes`
		: t`Has usado ${item.used} de ${item.limit} en total`;

	return (
		<Popover
			opened={opened}
			onChange={setOpened}
			position="bottom"
			withArrow
			shadow="md"
			closeOnClickOutside
		>
			<Popover.Target>
				<Text
					component="span"
					size="sm"
					c={color}
					style={{ cursor: "pointer" }}
					onMouseEnter={() => setOpened(true)}
					// onMouseLeave={() => setOpened(false)}
					onClick={() => setOpened((o) => !o)}
					className={classes.text}
				>
					{item.used}/{item.limit}
				</Text>
			</Popover.Target>

			<Popover.Dropdown>
				<Text size="sm" fw="bold">
					{featureLabels[item.feature] ?? item.label}
				</Text>

				<Text size="xs" c="dimmed" mb="xs">
					{periodLabel}
				</Text>

				<Progress value={percent} color={color} size="sm" />

				{nextPlan && (
					<Group gap="xs" justify="center" align="center" mt="sm">
						<Text size="xs">
							<Trans>Aumenta tu límite actualizando al plan</Trans>
						</Text>
						<PlanBadge plan={nextPlan.id} size="sm" />
					</Group>
				)}
			</Popover.Dropdown>
		</Popover>
	);
});

export default UsageLimit;
